import { useCallback } from "react";
import { Badge, DropDown, Box, Button, toast } from "dash-ui";
import { useTranslation } from "react-i18next";
import {
  GetPropertiesDocument,
  useSetPropertyStatusMutation,
  PropertyStatusesEnum as GraphQLPropertyStatusesEnum,
  GetPropertyByIdDocument,
} from "gql/generated.graphql";
import { PropertyStatusesEnum, getIsMarketOptional } from "dash-common";

import { IBadgePropertyStatusChange } from "./BadgePropertyStatusChange.interfaces";
import { BADGE_TO_STATUS_COLOR_MAP } from "./BadgePropertyStatusChange.constants";

const DROPDOWN_WIDTH = 114;
const BADGE_HEIGHT = 32;

function BadgePropertyStatusChange({
  currentStatus,
  propertyId,
  primaryOrganizationId,
  marketArea,
  location,
}: IBadgePropertyStatusChange) {
  const { t } = useTranslation("common", { keyPrefix: "labels" });
  const { t: tPages } = useTranslation("pages");
  const [setPropertyStatusMutation, { loading }] = useSetPropertyStatusMutation(
    {
      refetchQueries: [GetPropertiesDocument, GetPropertyByIdDocument],
    }
  );

  const onChangeStatus = useCallback(
    async (newStatus: PropertyStatusesEnum, closeDropdown: () => void) => {
      if (!primaryOrganizationId) {
        toast.error(tPages("propertyPage.noPrimaryOrganizationError"));
        return;
      }
      if (!getIsMarketOptional(location.address.state) && !marketArea) {
        toast.error(tPages("propertyPage.noMarketAreaError"));
        return;
      }
      if (!loading) {
        try {
          await setPropertyStatusMutation({
            variables: {
              input: {
                status: newStatus as unknown as GraphQLPropertyStatusesEnum,
                propertyId,
              },
            },
          });

          toast.success(tPages("propertyPage.visibilityStatusUpdateSuccess"));

          closeDropdown();
        } catch (error: any) {
          toast.error(
            error.message || tPages("propertyPage.visibilityStatusUpdateError")
          );

          console.error("Failed to update property status:", error);
        }
      }
    },
    [
      loading,
      propertyId,
      primaryOrganizationId,
      marketArea,
      location,
      setPropertyStatusMutation,
      tPages,
    ]
  );

  const statusOptions = [
    { value: PropertyStatusesEnum.DRAFT, label: t`draft` },
    { value: PropertyStatusesEnum.PUBLIC, label: t`public` },
    { value: PropertyStatusesEnum.PRIVATE, label: t`private` },
  ];

  return (
    <Box w={DROPDOWN_WIDTH}>
      <DropDown
        w={DROPDOWN_WIDTH}
        Anchor={({ isOpen }) => (
          <Badge
            h={BADGE_HEIGHT}
            square
            loading={loading}
            type={BADGE_TO_STATUS_COLOR_MAP[currentStatus]}
            iconRight={isOpen ? "arrowUp" : "arrowDown"}
            title={t(currentStatus.toLowerCase())}
          />
        )}
        optionsStyle={{
          maxWidth: "none",
        }}
      >
        {({ closeDropdown }) => (
          <Box py={1} style={{ width: `${DROPDOWN_WIDTH}px` }}>
            {statusOptions.map((option) => {
              const isCurrentStatus = option.value === currentStatus;

              return (
                <Box
                  key={option.value}
                  w="100%"
                  style={{
                    backgroundColor: isCurrentStatus
                      ? "#E3F2FD"
                      : "transparent",
                  }}
                >
                  <Button
                    variation="flat"
                    w="100%"
                    px={3}
                    py={2}
                    onClick={() => onChangeStatus(option.value, closeDropdown)}
                    disabled={loading}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span>{option.label}</span>
                    </span>
                  </Button>
                </Box>
              );
            })}
          </Box>
        )}
      </DropDown>
    </Box>
  );
}

export default BadgePropertyStatusChange;
